/**
 * Slug generation utilities for SEO-friendly URLs.
 * Handles Kazakh, Russian, and Latin text transliteration.
 */

const KAZAKH_MAP = {
  'ә': 'a', 'ғ': 'g', 'қ': 'k', 'ң': 'n', 'ө': 'o',
  'ұ': 'u', 'ү': 'u', 'һ': 'h', 'і': 'i',
  'Ә': 'A', 'Ғ': 'G', 'Қ': 'K', 'Ң': 'N', 'Ө': 'O',
  'Ұ': 'U', 'Ү': 'U', 'Һ': 'H', 'І': 'I',
};

const CYRILLIC_MAP = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
  'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z', 'и': 'i',
  'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
  'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
  'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch',
  'ш': 'sh', 'щ': 'shch', 'ъ': '', 'ы': 'y', 'ь': '',
  'э': 'e', 'ю': 'yu', 'я': 'ya',
  'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D',
  'Е': 'E', 'Ё': 'Yo', 'Ж': 'Zh', 'З': 'Z', 'И': 'I',
  'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M', 'Н': 'N',
  'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T',
  'У': 'U', 'Ф': 'F', 'Х': 'Kh', 'Ц': 'Ts', 'Ч': 'Ch',
  'Ш': 'Sh', 'Щ': 'Shch', 'Ъ': '', 'Ы': 'Y', 'Ь': '',
  'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya',
};

const MAX_SLUG_LENGTH = 60;

export function transliterate(text) {
  if (!text) return '';
  if (typeof text === 'object') {
    text = text.province || text.locality || '';
  }

  // Convert object to string (e.g., location objects)
  if (typeof text === 'object') {
    text = text?.province || text?.locality || text?.name || String(text);
  }

  if (typeof text !== 'string') return '';

  let result = '';
  for (const char of text) {
    if (KAZAKH_MAP[char] !== undefined) {
      result += KAZAKH_MAP[char];
    } else if (CYRILLIC_MAP[char] !== undefined) {
      result += CYRILLIC_MAP[char];
    } else if (/[a-zA-Z0-9]/.test(char)) {
      result += char;
    } else if (/[\s\-_]/.test(char)) {
      result += '-';
    }
    // discard any other characters
  }

  return result
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function generateSlug(title, location = null, id = null) {
  const locName = typeof location === 'object'
    ? (location?.province || location?.locality || null)
    : location;
  const parts = [];

  const titleSlug = transliterate(title);
  if (titleSlug) parts.push(titleSlug);

  const locationSlug = transliterate(locName);
  if (locationSlug) parts.push(locationSlug);

  let slug = parts.join('-');

  if (slug.length > MAX_SLUG_LENGTH) {
    slug = slug.substring(0, MAX_SLUG_LENGTH).replace(/-+$/, '');
  }

  if (id) {
    slug = slug ? `${slug}-${id}` : String(id);
  }

  return slug || '';
}

export function parseSlugUrl(slugOrId) {
  if (!slugOrId) return { id: null, slug: '' };
  const match = String(slugOrId).match(/^(.+)-(\d+)$/);
  if (match) {
    return { id: match[2], slug: match[1] };
  }
  const n = parseInt(slugOrId, 10);
  if (Number.isFinite(n)) return { id: String(n), slug: '' };
  return { id: null, slug: '' };
}